import styled, { keyframes } from "styled-components";
import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { agentState } from "../App";
import { db } from "../firebaseConfig";
import {
  collection, getDocs, setDoc, updateDoc, deleteDoc, doc,
} from "firebase/firestore";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupIcon from "@mui/icons-material/Group";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import ClearIcon from "@mui/icons-material/Clear";
import { IconButton } from "@mui/material";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const AVATAR_COLORS = [
  ["#dbeafe", "#1e40af"], ["#f3e8ff", "#6b21a8"], ["#dcfce7", "#166534"],
  ["#fef3c7", "#92400e"], ["#fce7f3", "#9d174d"], ["#e0f2fe", "#075985"],
];
const avatarColor = (name) => AVATAR_COLORS[
  Math.abs([...(name || "")].reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 0)) % AVATAR_COLORS.length
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AgentsTable() {
  const [agents,  setAgents]  = useRecoilState(agentState);
  const [form,    setForm]    = useState({ id: "", email: "", phone: "", percentage: "" });
  const [editId,  setEditId]  = useState(null);
  const [confirm, setConfirm] = useState(null); // id to confirm delete

  useEffect(() => {
    const fetchAgents = async () => {
      const snap = await getDocs(collection(db, "Agents"));
      setAgents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    fetchAgents();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleAdd = async () => {
    if (!form.id || !form.email || !form.phone) return alert("All fields are required!");
    const ref = doc(db, "Agents", form.id);
    await setDoc(ref, { Email: form.email, Phone: form.phone, Percentage: Number(form.percentage) });
    setAgents([...agents, { id: form.id, Email: form.email, Phone: form.phone, Percentage: Number(form.percentage) }]);
    setForm({ id: "", email: "", phone: "", percentage: "" });
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "Agents", id));
    setAgents(agents.filter((a) => a.id !== id));
    setConfirm(null);
  };

  const handleEdit = (agent) => {
    setEditId(agent.id);
    setForm({ id: agent.id, email: agent.Email || agent.Emal, phone: agent.Phone, percentage: agent.Percentage ?? "" });
  };

  const handleUpdate = async () => {
    if (!form.id || !form.email || !form.phone) return alert("All fields are required!");
    if (editId !== form.id) {
      await deleteDoc(doc(db, "Agents", editId));
      await setDoc(doc(db, "Agents", form.id), { Email: form.email, Phone: form.phone, Percentage: Number(form.percentage) });
    } else {
      await updateDoc(doc(db, "Agents", form.id), { Email: form.email, Phone: form.phone, Percentage: Number(form.percentage) });
    }
    setAgents(agents.map((a) => a.id === editId
      ? { id: form.id, Email: form.email, Phone: form.phone, Percentage: Number(form.percentage) }
      : a
    ));
    setEditId(null);
    setForm({ id: "", email: "", phone: "", percentage: "" });
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm({ id: "", email: "", phone: "", percentage: "" });
  };

  return (
    <Page>
      <PageTitle>Agents Management</PageTitle>

      <ContentLayout>
        {/* ── Left: Form ── */}
        <FormPanel>
          <FormHeader>
            <FormIconWrap>
              {editId
                ? <ModeEditIcon style={{ fontSize: 18 }} />
                : <PersonAddIcon style={{ fontSize: 18 }} />}
            </FormIconWrap>
            <FormTitle>{editId ? "Edit Agent" : "Add New Agent"}</FormTitle>
          </FormHeader>

          <FieldList>
            <Field>
              <label>Agent ID</label>
              <input name="id" value={form.id} onChange={handleChange}
                placeholder="e.g. Hamad" className="custom-input"
                disabled={!!editId}
              />
            </Field>
            <Field>
              <label>Email</label>
              <input name="email" value={form.email} onChange={handleChange}
                placeholder="agent@email.com" className="custom-input" type="email"
              />
            </Field>
            <Field>
              <label>Phone</label>
              <input name="phone" value={form.phone} onChange={handleChange}
                placeholder="+44 7700 000000" className="custom-input"
              />
            </Field>
          </FieldList>

          <FormActions>
            {editId && (
              <CancelBtn onClick={cancelEdit}>Cancel</CancelBtn>
            )}
            <SubmitBtn onClick={editId ? handleUpdate : handleAdd}>
              {editId ? "Update Agent" : "Add Agent"}
            </SubmitBtn>
          </FormActions>
        </FormPanel>

        {/* ── Right: Stats ── */}
        <StatsPanel>
          <StatCard color="#2563eb" bg="#eff6ff" border="#bfdbfe">
            <StatIcon color="#2563eb"><GroupIcon style={{ fontSize: 22 }} /></StatIcon>
            <StatBody>
              <StatLabel>Total Agents</StatLabel>
              <StatValue color="#2563eb">{agents.length}</StatValue>
            </StatBody>
          </StatCard>

          <StatCard color="#059669" bg="#f0fdf4" border="#bbf7d0">
            <StatIcon color="#059669"><EmailIcon style={{ fontSize: 22 }} /></StatIcon>
            <StatBody>
              <StatLabel>With Email</StatLabel>
              <StatValue color="#059669">
                {agents.filter(a => a.Email || a.Emal).length}
              </StatValue>
            </StatBody>
          </StatCard>

          <StatCard color="#7c3aed" bg="#f5f3ff" border="#ddd6fe">
            <StatIcon color="#7c3aed"><PhoneIcon style={{ fontSize: 22 }} /></StatIcon>
            <StatBody>
              <StatLabel>With Phone</StatLabel>
              <StatValue color="#7c3aed">
                {agents.filter(a => a.Phone).length}
              </StatValue>
            </StatBody>
          </StatCard>
        </StatsPanel>
      </ContentLayout>

      {/* ── Table ── */}
      <TableWrapper>
        <table className="agents-table">
          <thead>
            <tr>
              {["#", "Agent", "Email", "Phone", "Actions"].map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agents.length === 0 ? (
              <tr><td colSpan={5} className="empty">No agents found</td></tr>
            ) : agents.map((agent, idx) => {
              const [bg, fg] = avatarColor(agent.id);
              return (
                <tr key={agent.id} className={idx % 2 === 0 ? "even" : "odd"}>
                  <td className="sr">{idx + 1}</td>

                  <td>
                    <AgentCell>
                      <Avatar bg={bg} fg={fg}>{initials(agent.id)}</Avatar>
                      <AgentName>{agent.id}</AgentName>
                    </AgentCell>
                  </td>

                  <td>
                    <EmailCell>
                      <EmailIcon style={{ fontSize: 13, color: "#94a3b8" }} />
                      <span>{agent.Email || agent.Emal || "—"}</span>
                    </EmailCell>
                  </td>

                  <td>
                    <PhoneCell>
                      <PhoneIcon style={{ fontSize: 13, color: "#94a3b8" }} />
                      <span>{agent.Phone || "—"}</span>
                    </PhoneCell>
                  </td>

                  <td>
                    <ActionRow>
                      <EditBtn onClick={() => handleEdit(agent)}>
                        <ModeEditIcon style={{ fontSize: 14 }} />
                        Edit
                      </EditBtn>
                      <DeleteBtn onClick={() => setConfirm(agent.id)}>
                        <DeleteOutlineIcon style={{ fontSize: 14 }} />
                        Delete
                      </DeleteBtn>
                    </ActionRow>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="table-footer">
          <span>Showing <strong>{agents.length}</strong> agents</span>
        </div>
      </TableWrapper>

      {/* ── Delete Confirm Modal ── */}
      {confirm && (
        <ModalOverlay onClick={() => setConfirm(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <span>Delete Agent</span>
              <IconButton onClick={() => setConfirm(null)} size="small">
                <ClearIcon />
              </IconButton>
            </ModalHeader>
            <ConfirmText>
              Are you sure you want to delete agent <strong>{confirm}</strong>?
              This action cannot be undone.
            </ConfirmText>
            <ModalActions>
              <CancelBtn onClick={() => setConfirm(null)}>Cancel</CancelBtn>
              <DangerBtn onClick={() => handleDelete(confirm)}>
                <DeleteOutlineIcon style={{ fontSize: 16 }} />
                Delete Agent
              </DangerBtn>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </Page>
  );
}

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; }`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 25px auto 0;
  animation: ${fadeIn} 0.3s ease;

  .custom-input {
    padding: 9px 12px; font-size: 14px;
    border: 2px solid #ccc; border-radius: 6px;
    outline: none; width: 100%; box-sizing: border-box;
    background: white; transition: border-color 0.2s, box-shadow 0.2s;
    &:focus { border-color: #007bff; box-shadow: 0 0 4px rgba(0,123,255,0.25); }
    &::placeholder { color: #aaa; font-style: italic; }
    &:disabled { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; }
  }
`;

const PageTitle = styled.h2`
  font-size: 28px; font-weight: 700; color: #2563eb;
  background: #e0f2fe; display: inline-block;
  padding: 8px 20px; border-radius: 8px;
  margin-bottom: 28px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08); letter-spacing: 0.5px;
`;

const ContentLayout = styled.div`
  display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 28px;
`;

// ─── Form Panel ───────────────────────────────────────────────────────────────

const FormPanel = styled.div`
  background: white; border: 0.5px solid #e2e8f0;
  border-radius: 12px; padding: 22px 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  flex: 1; min-width: 300px; max-width: 480px;
`;

const FormHeader = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 20px;
`;
const FormIconWrap = styled.div`
  width: 32px; height: 32px; border-radius: 8px;
  background: #eff6ff; color: #2563eb;
  display: flex; align-items: center; justify-content: center;
`;
const FormTitle = styled.div`font-size: 15px; font-weight: 700; color: #1e293b;`;

const FieldList = styled.div`display: flex; flex-direction: column; gap: 14px;`;

const Field = styled.div`
  display: flex; flex-direction: column; gap: 5px;
  label {
    font-size: 12px; font-weight: 600; color: #555;
    text-transform: uppercase; letter-spacing: 0.4px;
  }
`;

const FormActions = styled.div`
  display: flex; gap: 10px; margin-top: 20px;
`;

const SubmitBtn = styled.button`
  flex: 1; padding: 10px 16px;
  background: #28a745; color: white;
  border: none; border-radius: 6px;
  font-size: 14px; font-weight: 600; cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #218838; }
`;

const CancelBtn = styled.button`
  padding: 10px 16px;
  background: white; color: #64748b;
  border: 1px solid #e2e8f0; border-radius: 6px;
  font-size: 14px; font-weight: 600; cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #f8fafc; }
`;

// ─── Stats Panel ──────────────────────────────────────────────────────────────

const StatsPanel = styled.div`
  display: flex; flex-direction: column; gap: 14px; flex: 1; min-width: 200px;
`;

const StatCard = styled.div`
  display: flex; align-items: center; gap: 14px;
  background: ${p => p.bg}; border: 1px solid ${p => p.border};
  border-radius: 12px; padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: transform 0.15s;
  &:hover { transform: translateY(-2px); }
`;
const StatIcon  = styled.div`
  width: 42px; height: 42px; border-radius: 10px;
  background: ${p => p.color}18; color: ${p => p.color};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;
const StatBody  = styled.div`display: flex; flex-direction: column; gap: 2px;`;
const StatLabel = styled.div`font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px; color: #64748b;`;
const StatValue = styled.div`font-size: 26px; font-weight: 700; color: ${p => p.color};`;

// ─── Table ────────────────────────────────────────────────────────────────────

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 10px;
  border: 0.5px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);

  .agents-table {
    width: 100%; border-collapse: collapse; font-size: 13px; min-width: 560px;

    thead tr {
      background: var(--color-background-tertiary, #f5f5f5);
      border-bottom: 1px solid #e0e0e0;
    }
    th {
      padding: 10px 14px; text-align: left;
      font-size: 11px; font-weight: 500; color: #888;
      letter-spacing: 0.6px; text-transform: uppercase; white-space: nowrap;
    }
    th:first-child { padding-left: 18px; }

    td { padding: 11px 14px; vertical-align: middle; border-bottom: 0.5px solid #ebebeb; }
    td:first-child { padding-left: 18px; }

    tr.even td { background: #fafafa; }
    tr.odd  td { background: #ffffff; }
    tr:hover td { background: #e8f4fd !important; transition: background 0.12s; }
    tr:last-child td { border-bottom: none; }

    .sr    { color: #aaa; font-size: 12px; width: 36px; }
    .empty { text-align: center; padding: 36px; color: #94a3b8; font-style: italic; font-size: 13px; }
  }

  .table-footer {
    display: flex; justify-content: flex-end; align-items: center;
    padding: 10px 18px; font-size: 13px; color: #888;
    border-top: 0.5px solid #ebebeb; background: #fafafa;
    border-radius: 0 0 10px 10px;
  }
`;

const AgentCell  = styled.div`display: flex; align-items: center; gap: 9px;`;
const Avatar     = styled.div`
  width: 32px; height: 32px; border-radius: 50%;
  background: ${p => p.bg}; color: ${p => p.fg};
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; flex-shrink: 0;
`;
const AgentName  = styled.span`font-weight: 600; color: #1e293b;`;

const EmailCell  = styled.div`display: flex; align-items: center; gap: 6px; color: #475569; font-size: 12px;`;
const PhoneCell  = styled.div`display: flex; align-items: center; gap: 6px; color: #475569; font-size: 12px;`;

const ActionRow  = styled.div`display: flex; gap: 8px; align-items: center;`;

const EditBtn = styled.button`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 10px; border-radius: 6px; font-size: 12px; font-weight: 600;
  border: 1px solid #bfdbfe; background: #eff6ff; color: #2563eb;
  cursor: pointer; transition: all 0.12s;
  &:hover { background: #dbeafe; border-color: #93c5fd; }
`;
const DeleteBtn = styled.button`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 10px; border-radius: 6px; font-size: 12px; font-weight: 600;
  border: 1px solid #fca5a5; background: #fef2f2; color: #dc2626;
  cursor: pointer; transition: all 0.12s;
  &:hover { background: #fee2e2; border-color: #f87171; }
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
`;
const ModalBox = styled.div`
  background: white; border-radius: 10px; padding: 28px;
  width: min(420px, 94vw);
  box-shadow: 0 8px 32px rgba(0,0,0,0.18); position: relative;
  animation: ${fadeIn} 0.2s ease;
`;
const ModalHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 16px;
  span { font-size: 18px; font-weight: 700; color: #111827; }
`;
const ConfirmText = styled.p`
  font-size: 14px; color: #475569; line-height: 1.6; margin-bottom: 24px;
  strong { color: #1e293b; }
`;
const ModalActions = styled.div`display: flex; gap: 10px;`;
const DangerBtn = styled.button`
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 16px; background: #dc2626; color: white;
  border: none; border-radius: 6px; font-size: 14px; font-weight: 600;
  cursor: pointer; transition: background 0.2s;
  &:hover { background: #b91c1c; }
`;